/**
 * breadcrumbHelper.js
 * Helper functions for generating breadcrumb navigation
 */

/**
 * Breadcrumb configuration mapping
 * Maps route patterns to breadcrumb configurations
 */
const breadcrumbMap = {
  // Users
  '/users': [
    { label: 'Atleti', path: '/users', icon: 'fas fa-users' }
  ],
  '/users/new': [
    { label: 'Atleti', path: '/users', icon: 'fas fa-users' },
    { label: 'Nuovo Atleta', path: '/users/new', icon: 'fas fa-user-plus' }
  ],
  '/users/:id/edit': [
    { label: 'Atleti', path: '/users', icon: 'fas fa-users' },
    { label: 'Modifica Atleta', path: null, icon: 'fas fa-user-edit' }
  ],
  
  // Courses
  '/courses': [
    { label: 'Corsi', path: '/courses', icon: 'fas fa-graduation-cap' }
  ],
  '/courses/new': [
    { label: 'Corsi', path: '/courses', icon: 'fas fa-graduation-cap' },
    { label: 'Nuovo Corso', path: '/courses/new', icon: 'fas fa-plus' }
  ],
  '/courses/:id': [
    { label: 'Corsi', path: '/courses', icon: 'fas fa-graduation-cap' },
    { label: 'Modifica Corso', path: null, icon: 'fas fa-edit' }
  ],
  
  // Course Offers
  '/course-offers': [
    { label: 'Abbonamenti', path: '/course-offers', icon: 'fas fa-tag' }
  ],
  '/course-offers/new': [
    { label: 'Abbonamenti', path: '/course-offers', icon: 'fas fa-tag' },
    { label: 'Nuovo Abbonamento', path: '/course-offers/new', icon: 'fas fa-plus' }
  ],
  '/course-offers/:id/edit': [
    { label: 'Abbonamenti', path: '/course-offers', icon: 'fas fa-tag' },
    { label: 'Modifica Abbonamento', path: null, icon: 'fas fa-edit' }
  ],
  
  // Groups
  '/groups': [
    { label: 'Gruppi', path: '/groups', icon: 'fas fa-users' }
  ],
  '/groups/:id/participants': [
    { label: 'Gruppi', path: '/groups', icon: 'fas fa-users' },
    { label: 'Partecipanti', path: null, icon: 'fas fa-user-friends' }
  ],
  '/groups/:groupId/rollcall': [
    { label: 'Gruppi', path: '/groups', icon: 'fas fa-users' },
    { label: 'Appello', path: null, icon: 'fas fa-clipboard-check' }
  ],
  
  // Certificates
  '/certificates': [
    { label: 'Certificati Medici', path: '/certificates', icon: 'fas fa-file-medical' }
  ],
  // Subscriptions (legacy route name)
  '/subscriptions': [
    { label: 'Abbonamenti', path: '/subscriptions', icon: 'fas fa-tag' }
  ],
  
  // Rentable Items
  '/rentable-items': [
    { label: 'Oggetti Noleggiabili', path: '/rentable-items', icon: 'fas fa-boxes' }
  ],
  '/rentable-items/new': [
    { label: 'Oggetti Noleggiabili', path: '/rentable-items', icon: 'fas fa-boxes' },
    { label: 'Nuovo Oggetto', path: '/rentable-items/new', icon: 'fas fa-plus' }
  ],
  '/rentable-items/:id/edit': [
    { label: 'Oggetti Noleggiabili', path: '/rentable-items', icon: 'fas fa-boxes' },
    { label: 'Modifica Oggetto', path: null, icon: 'fas fa-edit' }
  ],
  
  // Rentals
  '/rentals': [
    { label: 'Noleggi Attivi', path: '/rentals', icon: 'fas fa-clipboard-list' }
  ],
  '/rentals/new': [
    { label: 'Noleggi Attivi', path: '/rentals', icon: 'fas fa-clipboard-list' },
    { label: 'Nuovo Noleggio', path: '/rentals/new', icon: 'fas fa-plus' }
  ],
  '/rentals/:id/edit': [
    { label: 'Noleggi Attivi', path: '/rentals', icon: 'fas fa-clipboard-list' },
    { label: 'Modifica Noleggio', path: null, icon: 'fas fa-edit' }
  ],
  
  // Statistics
  '/statistics': [
    { label: 'Statistiche', path: '/statistics', icon: 'fas fa-chart-line' }
  ],

  // Audit
  '/audit': [
    { label: 'Audit Log', path: '/audit', icon: 'fas fa-history' }
  ],
  
  // Settings
  '/settings': [
    { label: 'Impostazioni', path: '/settings', icon: 'fas fa-cog' }
  ],

  // App Settings
  '/app-settings': [
    { label: 'Parametri di Sistema', path: '/app-settings', icon: 'fas fa-sliders-h' }
  ],

  // Profile
  '/profile': [
    { label: 'Profilo', path: '/profile', icon: 'fas fa-user-circle' }
  ]
};

/**
 * Match a pathname against a pattern with dynamic segments
 * @param {string} pathname - Current pathname
 * @param {string} pattern - Pattern to match against (e.g., '/users/:id/edit')
 * @returns {boolean} - Whether the pathname matches the pattern
 */
const matchPathPattern = (pathname, pattern) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const patternParts = pattern.split('/').filter(Boolean);
  
  if (pathParts.length !== patternParts.length) {
    return false;
  }
  
  return patternParts.every((part, index) => {
    if (part.startsWith(':')) {
      return true; // Dynamic segment, always matches
    }
    return part === pathParts[index];
  });
};

/**
 * Get breadcrumb configuration for a given pathname
 * @param {string} pathname - Current pathname
 * @returns {Array} - Array of breadcrumb items
 */
export const getBreadcrumbConfig = (pathname) => {
  // Don't show breadcrumb on dashboard
  if (pathname === '/' || pathname === '/dashboard') {
    return [];
  }
  
  // Try exact match first
  if (breadcrumbMap[pathname]) {
    return breadcrumbMap[pathname];
  }
  
  // Try pattern matching for dynamic routes
  for (const [pattern, breadcrumbs] of Object.entries(breadcrumbMap)) {
    if (matchPathPattern(pathname, pattern)) {
      return breadcrumbs;
    }
  }
  
  // Default: return empty array (no breadcrumb)
  return [];
};

/**
 * Add custom breadcrumb configuration
 * Useful for dynamic breadcrumbs that need runtime data
 * @param {string} path - Path pattern
 * @param {Array} breadcrumbs - Breadcrumb configuration
 */
export const addBreadcrumbConfig = (path, breadcrumbs) => {
  breadcrumbMap[path] = breadcrumbs;
};
